"use client";

import React from "react";
import { CloudRain, ShieldCheck, Building2, Hammer, Users, ArrowRight, CircleCheck as CheckCircle2, Award, HardHat } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function TemporaryRoofHeroImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-72 sm:h-96 bg-muted animate-pulse" />;
  }
  const photo = posts.find(p => p.title?.trim().toLowerCase() === "temporary roofs hero image" || p.customFields?.photo_name?.trim().toLowerCase() === "temporary roofs hero image");
  const isCustom = Boolean(photo?.customFields?.image || photo?.featuredImage);
  const imgSrc = photo?.customFields?.image || photo?.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/temporary_roof_scaffolding_structure.png";
  const imgAlt = photo?.customFields?.alt_text || photo?.title || "Temporary roof scaffold installation over residential property";
  const image = {
    src: imgSrc,
    srcset: isCustom ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/temporary_roof_scaffolding_structure.png-wvc-srcset",
    sizes: isCustom ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/temporary_roof_scaffolding_structure.png-wvc-sizes"
  };
  return <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={imgAlt} loading="lazy" decoding="async" className="w-full h-72 sm:h-96 object-cover" />;
}
export default function TemporaryRoofs() {
  const prefersReducedMotion = useReducedMotion();
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 20
  };
  const wp_query = new WP_Query(wp_query_params);
  const targetAudiences = [{
    title: "Roofing contractors",
    desc: "Dry site conditions to strip and re-cover roofs without weather downtime.",
    icon: Hammer
  }, {
    title: "Main contractors",
    desc: "Multi-trade weather protection integrated into larger commercial or residential programmes.",
    icon: Building2
  }, {
    title: "Property developers",
    desc: "Envelope protection on conversions, top-floor extensions, and structural alterations.",
    icon: HardHat
  }, {
    title: "Homeowners mid-renovation",
    desc: "Dependable protection for major domestic extensions and loft transformations.",
    icon: Users
  }];
  const useCases = [{
    title: "Re-roofing in poor weather",
    desc: "Complete cover allows continuous stripping, felting, and tiling regardless of rain forecast."
  }, {
    title: "Structural timber replacement",
    desc: "Exposed roof trusses, joists, and masonry remain dry while structural carpentry takes place."
  }, {
    title: "Extended refurbishment programmes",
    desc: "Reliable long-term protection for phased projects spanning several seasons."
  }];
  const deliverables = ["Planned around your programme to minimise unnecessary hire duration", "Safety-led erection and dismantling strictly to NASC standards", "Direct coordination with your roofing and construction teams on site", "Clear perimeter access and integrated hoist or loading platforms where needed"];
  const accreditations = [{
    name: "NASC Full Member Standards",
    icon: ShieldCheck
  }, {
    name: "CHAS Accredited Contractor",
    icon: Award
  }];
  return <section id="temporaryroofsoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Answer-First Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold px-3 py-1 bg-muted/50 border-border text-foreground">
            Weather protection scaffolding
          </Badge>
          
          <h1 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
            Temporary roofs
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6">
            BMF provides temporary roof and weather-protection scaffolding structures across London, Berkshire, Surrey, Hampshire, and Buckinghamshire for projects needing dependable weatherproof cover during re-roofing, refurbishment, or structural alterations.
          </p>

          <div className="flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Request a site survey
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent text-foreground">
              <Link to="/RoofingScaffoldingPage">
                View roofing scaffolds
              </Link>
            </Button>
          </div>
        </div>

        {/* Overview Grid: Image & What BMF Provides */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="relative overflow-hidden rounded-xl border border-border shadow-sm bg-card">
              <PostsProvider wp_query={{
              post_type: "site_photo",
              per_page: 20,
              status: ["publish"]
            }}>
                <TemporaryRoofHeroImage />
              </PostsProvider>
              <div className="p-4 bg-card/95 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>South East & Home Counties coverage</span>
                <span>Erected to NASC standards</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center">
            <Card className="border-border shadow-sm bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-card-foreground flex items-center gap-2">
                  <CloudRain className="size-5 text-primary" />
                  What BMF provides
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Engineered temporary roof systems designed to keep the project moving.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3.5">
                {deliverables.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-1" />
                    <span className="text-sm text-foreground leading-snug">{item}</span>
                  </div>)}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Who It's For & Typical Use Cases */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Who It's For */}
          <div className="flex flex-col">
            <div className="mb-4">
              <h2 className="font-default text-xl md:text-2xl font-bold text-foreground">
                Who it's for
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Supporting contractors and clients requiring watertight site environments.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 h-full">
              {targetAudiences.map((aud, i) => {
              const Icon = aud.icon;
              return <Card key={i} data-index={i} className="border-border shadow-xs bg-card flex flex-col justify-between">
                    <CardHeader className="p-5 pb-3">
                      <div className="size-9 rounded-md bg-accent flex items-center justify-center mb-2 text-foreground">
                        <Icon className="size-5 text-primary" />
                      </div>
                      <CardTitle className="text-base font-semibold text-card-foreground">
                        {aud.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-5 pt-0 text-xs sm:text-sm text-muted-foreground">
                      {aud.desc}
                    </CardContent>
                  </Card>;
            })}
            </div>
          </div>

          {/* Typical Use Cases */}
          <div className="flex flex-col">
            <div className="mb-4">
              <h2 className="font-default text-xl md:text-2xl font-bold text-foreground">
                Typical use cases
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Common scenarios where temporary weather containment is critical.
              </p>
            </div>
            <div className="space-y-4 h-full flex flex-col justify-between">
              {useCases.map((uc, i) => <div key={i} data-index={i} className="p-5 rounded-lg border border-border bg-card shadow-xs flex items-start gap-4 flex-1">
                  <div className="size-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5 font-semibold text-xs">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-base text-foreground mb-1">
                      {uc.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {uc.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Safety & Experience Credibility Footer */}
        <motion.div initial={prefersReducedMotion ? false : {
        opacity: 0,
        y: 16
      }} whileInView={prefersReducedMotion ? undefined : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4
      }} className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-xs">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-7 space-y-2">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <ShieldCheck className="size-4" />
                Safety & verified experience
              </div>
              <h3 className="font-default text-lg sm:text-xl font-bold text-foreground">
                Built to NASC standards with over 20 years of trade leadership
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Led by Pete Wood's 20+ years in the scaffolding industry, BMF delivers compliant, safety-first temporary roofing systems. Every structure is planned, built, and inspected to rigorous standards with certified industry accreditations.
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
              {accreditations.map((acc, i) => {
              const Icon = acc.icon;
              return <div key={i} data-index={i} className="flex items-center gap-3 p-3 rounded-md bg-background border border-border text-sm font-medium text-foreground">
                    <Icon className="size-4 text-primary shrink-0" />
                    <span>{acc.name}</span>
                  </div>;
            })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}